import {IssueRelativeTime} from '@github-ui/ago'
import {ariaLabelDate} from '@github-ui/aria-label-date'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {VALUES} from '@github-ui/timeline-items/Values'
import {KebabHorizontalIcon} from '@primer/octicons-react'
import {ActionList, AnchoredOverlay, IconButton} from '@primer/react'
import type {MouseEvent} from 'react'
import {useState} from 'react'

import styles from './ActivityCommentIndicator.module.css'
import type {ActivityCommentSummary} from './fetch-timeline-items'

export function ActivityCommentIndicator({
  comment,
  issueUrl,
  onOpen,
}: {
  comment: ActivityCommentSummary
  issueUrl: string
  onOpen: (databaseId: number) => void
}) {
  const databaseId = comment.databaseId
  return (
    <div className={styles.Indicator} data-testid={`activity-comment-${databaseId}`}>
      <div className={styles.Header}>
        {isFeatureEnabled('issue_inline_avatars') && (
          <GitHubAvatar src={comment.author?.avatarUrl ?? VALUES.ghost.avatarUrl} alt="" size={24} />
        )}
        <div className={styles.SummaryLine}>
          <h3 className={styles.Summary} tabIndex={-1}>
            <strong className={styles.Author}>{comment.author?.login ?? 'ghost'}</strong> commented
          </h3>
          <span className={styles.Timestamp}>
            <IssueRelativeTime datetime={comment.createdAt} />
          </span>
        </div>
        {databaseId !== null && (
          <ActivityCommentActions
            databaseId={databaseId}
            issueUrl={issueUrl}
            authorLogin={comment.author?.login ?? 'ghost'}
            createdAt={comment.createdAt}
            onOpen={onOpen}
          />
        )}
      </div>
      {databaseId === null && <span>Comment unavailable</span>}
    </div>
  )
}

function handleCommentLinkClick(
  event: MouseEvent<HTMLAnchorElement>,
  databaseId: number,
  onOpen: (id: number) => void,
) {
  // Modified clicks retain the browser's native link behavior.
  // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
  if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
  event.preventDefault()
  onOpen(databaseId)
}

function ActivityCommentActions({
  databaseId,
  issueUrl,
  authorLogin,
  createdAt,
  onOpen,
}: {
  databaseId: number
  issueUrl: string
  authorLogin: string
  createdAt: string
  onOpen: (id: number) => void
}) {
  const [open, setOpen] = useState(false)
  const createdAtFormatted = ariaLabelDate(new Date(createdAt))
  return (
    <AnchoredOverlay
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      align="end"
      displayInViewport
      overlayProps={{role: 'dialog', 'aria-label': 'Comment actions'}}
      renderAnchor={anchorProps => (
        <IconButton
          {...anchorProps}
          icon={KebabHorizontalIcon}
          variant="invisible"
          size="small"
          aria-haspopup="dialog"
          aria-label={`Actions for comment by ${authorLogin}, ${createdAtFormatted}`}
        />
      )}
    >
      <ActionList>
        <ActionList.LinkItem
          href={`${issueUrl}#issuecomment-${databaseId}`}
          data-comment-jump
          onClick={event => {
            setOpen(false)
            handleCommentLinkClick(event, databaseId, onOpen)
          }}
        >
          View full comment
        </ActionList.LinkItem>
      </ActionList>
    </AnchoredOverlay>
  )
}
